// frontend/src/pages/Orders.js
import React, { useState, useEffect } from 'react';
import { api, channelLabel } from '../App';

function getTrackingUrl(trackingNumber, carrier) {
  if (!trackingNumber) return null;
  const tn = trackingNumber.trim();
  // Auto-detect if no carrier specified
  let c = (carrier || '').toUpperCase();
  if (!c || c === 'AUTO-DETECT') {
    if (/^1Z/i.test(tn)) c = 'UPS';
    else if (/^96\d{10,}$/.test(tn) || /^\d{12}$/.test(tn) || /^\d{15}$/.test(tn)) c = 'FEDEX';
    else c = 'USPS'; // default to USPS
  }
  if (c === 'UPS') return `https://www.ups.com/track?tracknum=${tn}`;
  if (c === 'FEDEX') return `https://www.fedex.com/fedextrack/?trknbr=${tn}`;
  return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${tn}`;
}

function TrackingLink({ trackingNumber, carrier }) {
  if (!trackingNumber) return null;
  const url = getTrackingUrl(trackingNumber, carrier);
  const c = (carrier || 'USPS').toUpperCase();
  const colors = { USPS: '#333399', UPS: '#644117', FEDEX: '#4D148C' };
  const bg = { USPS: 'rgba(51,51,153,.12)', UPS: 'rgba(100,65,23,.12)', FEDEX: 'rgba(77,20,140,.12)' };
  return (
    <a href={url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontFamily: 'monospace',
        padding: '2px 8px', borderRadius: 4, background: bg[c] || bg.USPS, color: colors[c] || colors.USPS,
        textDecoration: 'none', fontWeight: 600 }}
      title={`Track on ${carrier || 'USPS'}`}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
      </svg>
      {trackingNumber}
    </a>
  );
}

function TrackingModal({ order, onSave, onClose }) {
  const [tracking, setTracking] = useState('');
  const [carrier, setCarrier]   = useState('Auto-detect');
  const [saving, setSaving]     = useState(false);
  const CARRIERS = ['Auto-detect', 'USPS', 'UPS', 'FedEx', 'DHL', 'Other'];

  async function save() {
    if (!tracking.trim()) return;
    setSaving(true);
    await api.patch(`/orders/${order.id}/tracking`, {
      tracking_number: tracking.trim(),
      carrier: carrier === 'Auto-detect' ? '' : carrier,
    });
    setSaving(false);
    onSave();
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div className="card" style={{ width:'100%', maxWidth:400, margin:16 }}>
        <div style={{ fontWeight:700, fontSize:16, marginBottom:4 }}>📮 Add Tracking</div>
        <div style={{ fontSize:12, color:'var(--muted)', marginBottom:16 }}>{order.sku} · {order.buyer_name}</div>
        <div className="form-group">
          <label>Tracking Number</label>
          <input value={tracking} onChange={e => setTracking(e.target.value)} placeholder="Enter tracking number" autoFocus
            onKeyDown={e => e.key === 'Enter' && save()} />
        </div>
        <div className="form-group">
          <label>Carrier</label>
          <select value={carrier} onChange={e => setCarrier(e.target.value)}>
            {CARRIERS.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div style={{ display:'flex', gap:8, marginTop:4 }}>
          <button className="btn btn-primary" onClick={save} disabled={saving || !tracking.trim()}>
            {saving ? '⏳ Saving…' : '✓ Save & Mark Shipped'}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function EditOrderModal({ order, onSave, onClose }) {
  const [form, setForm] = useState({
    buyer_name: order.buyer_name || '',
    sale_price: order.sale_price || '',
    quantity: order.quantity_sold || 1,
    channel: order.channel || '',
    sku: order.sku || '',
    title: order.title || '',
  });
  const [saving, setSaving] = useState(false);
  const EDIT_CHANNELS = ['poshmark','mercari','facebook','etsy','other'];

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  async function save() {
    setSaving(true);
    await api.put(`/orders/${order.id}`, form);
    setSaving(false);
    onSave();
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div className="card" style={{ width:'100%', maxWidth:420, margin:16 }}>
        <div style={{ fontWeight:700, fontSize:16, marginBottom:4 }}>Edit Order</div>
        <div style={{ fontSize:12, color:'var(--muted)', marginBottom:16 }}>{order.platform_order_id}</div>
        <div className="form-group">
          <label>Buyer Name</label>
          <input value={form.buyer_name} onChange={e => set('buyer_name', e.target.value)} />
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <div className="form-group" style={{ flex:1 }}>
            <label>Sale Price</label>
            <input type="number" step="0.01" value={form.sale_price} onChange={e => set('sale_price', e.target.value)} />
          </div>
          <div className="form-group" style={{ flex:1 }}>
            <label>Quantity</label>
            <input type="number" min="1" value={form.quantity} onChange={e => set('quantity', e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label>Channel</label>
          <select value={form.channel} onChange={e => set('channel', e.target.value)}>
            {EDIT_CHANNELS.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>SKU</label>
          <input value={form.sku} onChange={e => set('sku', e.target.value)} className="mono" />
        </div>
        <div className="form-group">
          <label>Item Title</label>
          <input value={form.title} onChange={e => set('title', e.target.value)} />
        </div>
        <div style={{ display:'flex', gap:8, marginTop:4 }}>
          <button className="btn btn-primary" onClick={save} disabled={saving || !form.buyer_name.trim()}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function OrderDetail({ order, onClose, onStatusUpdate, onDelete, onShip, onEdit, onReopen }) {
  let img = null;
  try { img = JSON.parse(order.images || '[]')[0] || null; } catch {}

  let address = null;
  try { address = JSON.parse(order.buyer_address || '{}'); } catch {}

  const statusColors = {
    pending: { bg: 'rgba(255,214,10,.15)', color: 'var(--warn)', label: 'Pending' },
    picked:  { bg: 'rgba(0,212,170,.15)', color: 'var(--accent2)', label: 'Picked' },
    shipped: { bg: 'rgba(108,99,255,.15)', color: 'var(--accent)', label: 'Shipped' },
    cancelled: { bg: 'rgba(122,127,148,.15)', color: 'var(--muted)', label: 'Cancelled' },
  };
  const sc = statusColors[order.order_status] || statusColors.pending;

  const paidDate = order.created_at ? new Date(order.created_at) : null;
  const shipByDate = order.ship_by_date ? new Date(order.ship_by_date) : null;
  const isLate = shipByDate && shipByDate <= new Date(Date.now() + 86400000);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="card" style={{ width: '100%', maxWidth: 520, margin: 16, maxHeight: '90vh', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>
              {channelLabel(order.channel)}
            </div>
            <div style={{ fontWeight: 700, fontSize: 18 }}>Order Details</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 20,
            color: 'var(--muted)', cursor: 'pointer', padding: '4px 8px' }}>X</button>
        </div>

        {/* Status badge */}
        <div style={{ display: 'inline-block', padding: '4px 14px', borderRadius: 99, fontSize: 13, fontWeight: 700,
          background: sc.bg, color: sc.color, marginBottom: 16 }}>
          {sc.label}
        </div>

        {/* Item card */}
        <div style={{ display: 'flex', gap: 14, padding: 14, background: 'var(--bg)', border: '1px solid var(--border)',
          borderRadius: 10, marginBottom: 16 }}>
          {img
            ? <img src={img} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
            : <div style={{ width: 80, height: 80, borderRadius: 8, background: 'var(--surface2)', display: 'flex',
                alignItems: 'center', justifyContent: 'center', fontSize: 28, flexShrink: 0, color: 'var(--muted)' }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
                </svg>
              </div>
          }
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, lineHeight: 1.3 }}>{order.title || 'Untitled'}</div>
            <div style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--accent2)', marginBottom: 4 }}>{order.sku}</div>
            <div style={{ display: 'flex', gap: 16, fontSize: 13, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, color: 'var(--accent2)' }}>${parseFloat(order.sale_price || 0).toFixed(2)}</span>
              <span style={(order.quantity_sold || 1) > 1 ? { fontWeight: 700, color: '#fbbf24' } : { color: 'var(--muted)' }}>
                Qty: {order.quantity_sold || 1}
              </span>
              {order.combinable_count > 0 && (
                <span title="Same buyer has other open orders — may ship together"
                  style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, fontWeight: 700,
                    background: 'rgba(251,191,36,.18)', color: '#fbbf24' }}>
                  Combine +{order.combinable_count}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Payment breakdown — parsed from raw_data, only shown when available */}
        {order.breakdown && (order.breakdown.item_subtotal != null || order.breakdown.shipping != null || order.breakdown.total != null) && (
          <div style={{ padding: 12, background: 'var(--bg)', border: '1px solid var(--border)',
            borderRadius: 10, marginBottom: 16, fontSize: 13 }}>
            <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase',
              letterSpacing: '.05em', marginBottom: 6 }}>Payment breakdown</div>
            {[
              ['Item subtotal',   order.breakdown.item_subtotal],
              ['Shipping',        order.breakdown.shipping],
              ['Tax (collected)', order.breakdown.tax],
            ].map(([label, val], i) => val != null && (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', color: 'var(--muted)' }}>
                <span>{label}</span>
                <span style={{ fontFamily: 'monospace' }}>${val.toFixed(2)}</span>
              </div>
            ))}
            {order.breakdown.total != null && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0 2px 0',
                borderTop: '1px solid var(--border)', marginTop: 4, fontWeight: 700 }}>
                <span>Buyer paid total</span>
                <span style={{ fontFamily: 'monospace', color: 'var(--accent2)' }}>${order.breakdown.total.toFixed(2)}</span>
              </div>
            )}
            <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 6, fontStyle: 'italic' }}>
              sale_price column = {order.breakdown.source === 'ebay' ? 'item + shipping (excludes tax)' : 'item only (excludes shipping & tax)'}
            </div>
          </div>
        )}

        {/* Timeline */}
        <div style={{ marginBottom: 20, padding: '0 4px' }}>
          {[
            { label: 'Buyer paid', date: paidDate, done: true },
            { label: 'Ship by', date: shipByDate, done: ['shipped'].includes(order.order_status), urgent: isLate && order.order_status !== 'shipped' },
            { label: 'Delivered', date: null, done: false },
          ].map((step, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, marginBottom: i < 2 ? 0 : 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20 }}>
                <div style={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                  background: step.done ? 'var(--accent2)' : step.urgent ? 'var(--danger)' : 'var(--border)',
                  border: step.done ? 'none' : '2px solid var(--border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {step.done && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                </div>
                {i < 2 && <div style={{ width: 2, height: 28, background: step.done ? 'var(--accent2)' : 'var(--border)' }} />}
              </div>
              <div style={{ paddingBottom: i < 2 ? 16 : 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: step.urgent ? 'var(--danger)' : 'var(--text)' }}>{step.label}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                  {step.date ? step.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '--'}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Order info */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase',
            letterSpacing: '.05em', marginBottom: 8 }}>Order Info</div>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '6px 12px', fontSize: 13 }}>
            <span style={{ color: 'var(--muted)' }}>Order #</span>
            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{order.platform_order_id}</span>
            <span style={{ color: 'var(--muted)' }}>Buyer</span>
            <span style={{ fontWeight: 600 }}>{order.buyer_name || '--'}</span>
            <span style={{ color: 'var(--muted)' }}>Bin Location</span>
            <span style={{ fontFamily: 'monospace', color: 'var(--accent)' }}>{order.sku_bin || order.location || '--'}</span>
            {order.tracking_number && <>
              <span style={{ color: 'var(--muted)' }}>Tracking</span>
              <span><TrackingLink trackingNumber={order.tracking_number} carrier={order.carrier} /></span>
            </>}
          </div>
        </div>

        {/* Shipping address */}
        {address && (address.street || address.city) && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase',
              letterSpacing: '.05em', marginBottom: 8 }}>Ship To</div>
            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, fontSize: 13, lineHeight: 1.6 }}>
              {order.buyer_name && <div style={{ fontWeight: 600 }}>{order.buyer_name}</div>}
              {address.street && <div>{address.street}</div>}
              {address.street2 && <div>{address.street2}</div>}
              {(address.city || address.state || address.zip) && (
                <div>{[address.city, address.state].filter(Boolean).join(', ')} {address.zip}</div>
              )}
              {address.country && address.country !== 'US' && address.country !== 'United States' && (
                <div>{address.country}</div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 8, borderTop: '1px solid var(--border)' }}>
          {order.order_status === 'pending' && (
            <button className="btn btn-success" style={{ fontSize: 13 }}
              onClick={() => { onStatusUpdate(order.id, 'picked'); onClose(); }}>
              Mark Picked
            </button>
          )}
          {order.order_status === 'picked' && (
            <button className="btn btn-primary" style={{ fontSize: 13 }}
              onClick={() => { onShip(order); onClose(); }}>
              Add Tracking
            </button>
          )}
          {order.order_status === 'shipped' && (
            <button className="btn btn-ghost" style={{ fontSize: 13 }}
              onClick={() => { onReopen(order); }}>
              Reopen &amp; Re-pick
            </button>
          )}
          {['pending', 'picked'].includes(order.order_status) && (
            <>
              <button className="btn btn-ghost" style={{ fontSize: 13 }}
                onClick={() => { onEdit(order); onClose(); }}>
                Edit
              </button>
              <button className="btn btn-ghost" style={{ fontSize: 13, color: 'var(--danger)' }}
                onClick={() => { onDelete(order); }}>
                Delete
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const STATUSES    = ['all','pending','picked','shipped','cancelled'];
const CHANNELS    = ['all','ebay_source','ebay_store2','walmart','amazon','mercari','poshmark'];
const STATUS_BADGE = { pending:'badge-pending', picked:'badge-active', shipped:'badge-shipped', cancelled:'badge-ended' };

function SortHeader({ label, field, sortField, sortDir, onSort, width }) {
  const active = sortField === field;
  return (
    <th onClick={() => onSort(field)} style={{ cursor:'pointer', userSelect:'none', whiteSpace:'nowrap',
      width: width || 'auto', minWidth: 60, overflow:'hidden', resize:'horizontal',
      borderRight:'2px solid transparent' }}>
      {label}
      <span style={{ marginLeft:4, fontSize:10, color: active ? 'var(--accent)' : 'var(--muted)' }}>
        {active ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
      </span>
    </th>
  );
}

export default function Orders() {
  const [orders, setOrders]           = useState([]);
  const [total, setTotal]             = useState(0);
  const [status, setStatus]           = useState('pending');
  const [channel, setChannel]         = useState('all');
  const [search, setSearch]           = useState('');
  const [days, setDays]               = useState('60');
  const [trackingOrder, setTrackingOrder] = useState(null);
  const [editOrder, setEditOrder]     = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);
  const [sortField, setSortField]     = useState('created_at');
  const [sortDir, setSortDir]         = useState('desc');

  // Debounced search — fires 300ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      const p = new URLSearchParams();
      if (status !== 'all')  p.set('status', status);
      if (channel !== 'all') p.set('channel', channel);
      if (search.trim())     p.set('search', search.trim());
      if (days)              p.set('days', days);
      api.get(`/orders?${p}`).then(r => {
        setOrders(r.data.orders);
        setTotal(r.data.total);
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [search, status, channel, days]);

  function load() {
    const p = new URLSearchParams();
    if (status !== 'all')  p.set('status', status);
    if (channel !== 'all') p.set('channel', channel);
    if (search.trim())     p.set('search', search.trim());
    if (days)              p.set('days', days);
    api.get(`/orders?${p}`).then(r => { setOrders(r.data.orders); setTotal(r.data.total); });
  }

  function handleSort(field) {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  const sorted = [...orders].sort((a, b) => {
    let aVal = a[sortField] || '';
    let bVal = b[sortField] || '';
    // Numeric fields
    if (sortField === 'quantity_sold' || sortField === 'sale_price') {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    }
    // Date fields
    if (sortField === 'created_at' || sortField === 'ship_by_date') {
      aVal = aVal ? new Date(aVal).getTime() : 0;
      bVal = bVal ? new Date(bVal).getTime() : 0;
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    }
    // String fields
    return sortDir === 'asc'
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });

  function updateStatus(id, s) {
    api.patch(`/orders/${id}/status`, { status: s }).then(load);
  }

  function deleteOrder(o) {
    if (!window.confirm(`Delete order ${o.platform_order_id}?\n\nSKU: ${o.sku}\nBuyer: ${o.buyer_name}\n\nInventory will be restored if pending/picked.`)) return;
    api.delete(`/orders/${o.id}`).then(load);
  }

  function reopenOrder(o) {
    const trk = o.tracking_number ? `\n\nTracking ${o.tracking_number} will be cleared.` : '';
    if (!window.confirm(
      `Send order ${o.platform_order_id} back to the pick list to be picked and shipped again?${trk}`
      + `\n\nOne unit of ${o.sku} will be removed from stock and pushed down on all channels, `
      + `so the lost unit can't sell again before the replacement ships.`
    )) return;
    api.post(`/orders/${o.id}/reopen`).then(r => {
      const d = r && r.data;
      if (d && d.inventory_error) {
        window.alert(`Order reopened, but the stock decrement FAILED:\n\n${d.inventory_error}\n\n`
          + `Reduce ${d.sku} by ${d.decremented_by} manually before picking.`);
      } else if (d && d.new_quantity != null) {
        window.alert(`Order reopened and back in the pick list.\n\n${d.sku} stock is now ${d.new_quantity}.`);
      }
      load();
    }).catch(e => {
      window.alert((e && e.response && e.response.data && e.response.data.error) || 'Reopen failed');
    });
  }

  const sortProps = { sortField, sortDir, onSort: handleSort };

  // Summary counts
  const pendingCount = orders.filter(o => o.order_status === 'pending').length;
  const pickedCount = orders.filter(o => o.order_status === 'picked').length;
  const todayRevenue = orders.filter(o => o.created_at && new Date(o.created_at).toDateString() === new Date().toDateString())
    .reduce((s, o) => s + (parseFloat(o.sale_price) || 0), 0);

  const CHANNEL_COLORS = {
    ebay_source: { bg: 'rgba(108,99,255,.12)', color: 'var(--accent)' },
    ebay_store2: { bg: 'rgba(0,212,170,.12)', color: 'var(--accent2)' },
    walmart:     { bg: 'rgba(0,120,215,.12)', color: '#0078d7' },
    amazon:      { bg: 'rgba(255,153,0,.12)',  color: '#ff9900' },
    poshmark:    { bg: 'rgba(212,67,108,.12)', color: '#d4436c' },
    mercari:     { bg: 'rgba(255,107,107,.12)', color: '#ff6b6b' },
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Orders <span className="text-muted" style={{ fontSize:14, fontWeight:400 }}>({total})</span></div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Pending', value: pendingCount, color: 'var(--warn)' },
          { label: 'Picked', value: pickedCount, color: 'var(--accent2)' },
          { label: 'Total Shown', value: total, color: 'var(--accent)' },
          { label: "Today's Revenue", value: `$${todayRevenue.toFixed(2)}`, color: 'var(--accent2)' },
        ].map(s => (
          <div key={s.label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
            padding: '14px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Search + Filters */}
      <div className="inv-toolbar">
        <div className="inv-search" style={{ maxWidth: 360 }}>
          <span className="inv-search-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </span>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search order #, buyer, SKU, or item..." />
          {search && <button type="button" className="btn btn-ghost"
            style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', padding:'4px 8px', fontSize:11 }}
            onClick={() => setSearch('')}>Clear</button>}
        </div>
      </div>

      {/* Status tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        {STATUSES.map(s => (
          <button key={s} className={`inv-filter-btn ${status === s ? 'active' : ''}`}
            onClick={() => setStatus(s)}>{s}</button>
        ))}
      </div>
      {/* Channel tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        {CHANNELS.map(c => (
          <button key={c} className={`inv-filter-btn ${channel === c ? 'active' : ''}`}
            onClick={() => setChannel(c)}>{c === 'all' ? 'All Channels' : channelLabel(c)}</button>
        ))}
      </div>
      {/* Date range tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: 'var(--muted)', marginRight: 4 }}>Show:</span>
        {[
          { v: '30',  label: 'Last 30 days' },
          { v: '60',  label: 'Last 60 days' },
          { v: '90',  label: 'Last 90 days' },
          { v: 'all', label: 'All time' },
        ].map(d => (
          <button key={d.v} className={`inv-filter-btn ${days === d.v ? 'active' : ''}`}
            onClick={() => setDays(d.v)}>{d.label}</button>
        ))}
      </div>

      {orders.length === 0 ? (
        <div className="empty-state">
          <div style={{ fontSize: 48, marginBottom: 12, opacity: .5 }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M16.5 9.4l-9-5.19M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
            </svg>
          </div>
          <div>No orders found</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {sorted.map(o => {
            let img = null;
            try { img = JSON.parse(o.images || '[]')[0]; } catch {}
            const urgent = isUrgent(o.ship_by_date);
            const cc = CHANNEL_COLORS[o.channel] || { bg: 'rgba(122,127,148,.1)', color: 'var(--muted)' };

            return (
              <div key={o.id} onClick={() => setDetailOrder(o)}
                style={{
                  display: 'flex', gap: 14, padding: '14px 16px',
                  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
                  cursor: 'pointer', transition: 'border-color .15s',
                  borderLeft: urgent ? '3px solid var(--danger)' : o.order_status === 'picked' ? '3px solid var(--accent2)' : '1px solid var(--border)',
                }}>

                {/* Image */}
                <div style={{ flexShrink: 0, width: 56, height: 56 }}>
                  {img
                    ? <img src={img} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8 }} />
                    : <div style={{ width: 56, height: 56, borderRadius: 8, background: 'var(--bg)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', color: 'var(--muted)' }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
                        </svg>
                      </div>
                  }
                </div>

                {/* Main info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                      {o.title || 'Untitled'}
                    </div>
                    <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--accent2)', flexShrink: 0 }}>
                      ${parseFloat(o.sale_price || 0).toFixed(2)}
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 11, background: 'rgba(108,99,255,.12)',
                      color: 'var(--accent)', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
                      {o.sku_bin || '?'}
                    </span>
                    <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--muted)' }}>{o.sku}</span>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                      background: cc.bg, color: cc.color }}>{channelLabel(o.channel)}</span>
                  </div>

                  <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--muted)', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span>Buyer: <strong style={{ color: 'var(--text)' }}>{o.buyer_name || '--'}</strong></span>
                    {o.combinable_count > 0 && (
                      <span title="Same buyer has other open orders — may ship together"
                        style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, fontWeight: 700,
                          background: 'rgba(251,191,36,.18)', color: '#fbbf24' }}>
                        Combine +{o.combinable_count}
                      </span>
                    )}
                    <span style={o.quantity_sold > 1 ? { fontWeight: 700, color: '#fbbf24' } : undefined}>
                      Qty: {o.quantity_sold}
                    </span>
                    <span>{o.created_at ? new Date(o.created_at).toLocaleDateString() : ''}</span>
                    {o.ship_by_date && (
                      <span style={{ color: urgent ? 'var(--danger)' : 'var(--muted)', fontWeight: urgent ? 700 : 400 }}>
                        Ship by {new Date(o.ship_by_date).toLocaleDateString()}
                      </span>
                    )}
                    {o.tracking_number && (
                      <TrackingLink trackingNumber={o.tracking_number} carrier={o.carrier} />
                    )}
                  </div>
                </div>

                {/* Status + Actions */}
                <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}
                  onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className={`badge ${STATUS_BADGE[o.order_status] || ''}`}
                      style={{ fontSize: 11, padding: '3px 10px' }}>{o.order_status}</span>
                  </div>
                  {o.order_status === 'pending' && (
                    <button className="btn btn-success" style={{ padding: '4px 10px', fontSize: 11 }}
                      onClick={() => updateStatus(o.id, 'picked')}>Picked</button>
                  )}
                  {o.order_status === 'picked' && (
                    <button className="btn btn-primary" style={{ padding: '4px 10px', fontSize: 11 }}
                      onClick={() => setTrackingOrder(o)}>Ship</button>
                  )}
                  {o.order_status === 'shipped' && (
                    <button className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: 11, whiteSpace: 'nowrap' }}
                      title="Send back to the pick list to be picked and shipped again (removes one unit from stock)"
                      onClick={() => reopenOrder(o)}>Reopen &amp; Re-pick</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {trackingOrder && (
        <TrackingModal
          order={trackingOrder}
          onSave={() => { setTrackingOrder(null); load(); }}
          onClose={() => setTrackingOrder(null)}
        />
      )}

      {editOrder && (
        <EditOrderModal
          order={editOrder}
          onSave={() => { setEditOrder(null); load(); }}
          onClose={() => setEditOrder(null)}
        />
      )}

      {detailOrder && (
        <OrderDetail
          order={detailOrder}
          onClose={() => setDetailOrder(null)}
          onStatusUpdate={(id, s) => { updateStatus(id, s); setDetailOrder(null); }}
          onDelete={(o) => { deleteOrder(o); setDetailOrder(null); }}
          onShip={(o) => { setDetailOrder(null); setTrackingOrder(o); }}
          onEdit={(o) => { setDetailOrder(null); setEditOrder(o); }}
          onReopen={(o) => { reopenOrder(o); setDetailOrder(null); }}
        />
      )}
    </div>
  );
}

function isUrgent(d) { return d && new Date(d) <= new Date(Date.now() + 86400000); }
